package com.example.mystybag.communication

import com.example.mystybag.communication.defines.FileUploadTarget
import com.example.mystybag.communication.ext.CommandResult
import com.example.mystybag.communication.internal.getRequestTimestampMs
import com.example.mystybag.sharedmodels.AllCommands.COMMAND_GET_WELCOME_IMAGES
import com.example.mystybag.sharedmodels.AllCommands.FORGOTTEN_PASSWORD_API
import com.example.mystybag.sharedmodels.AllCommands.GET_COUNTRIES_API
import com.example.mystybag.sharedmodels.AllCommands.MERCHANT_LOGIN
import com.example.mystybag.sharedmodels.AllCommands.STAFF_LOGIN
import com.example.mystybag.sharedmodels.AllCommands.USER_LOGIN
import com.example.mystybag.sharedmodels.AllCommands.UPLOAD_FILE
import com.example.mystybag.sharedmodels.ModelsData
import com.example.mystybag.sharedmodels.base.BaseRequestModel
import com.example.mystybag.sharedmodels.base.BaseResponseModel
import com.example.mystybag.sharedmodels.common.responseModels.UploadFileResponseModel
import com.example.mystybag.sharedmodels.defines.ServerStatusDefines.Companion.STATUS_EXCEPTION
import com.example.mystybag.sharedmodels.defines.ServerStatusDefines.Companion.STATUS_NO_INTERNET_CONNECTION
import com.example.mystybag.sharedmodels.defines.ServerStatusDefines.Companion.STATUS_OK
import com.example.mystybag.sharedmodels.defines.ServerStatusDefines.Companion.STATUS_TOKEN_IS_EXPIRED
import io.ktor.client.HttpClient
import io.ktor.client.request.forms.formData
import io.ktor.client.request.forms.submitFormWithBinaryData
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.Headers
import io.ktor.http.contentType
import io.ktor.http.userAgent
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.serialization.InternalSerializationApi
import kotlinx.serialization.json.Json
import kotlinx.serialization.serializer
import com.example.mystybag.communication.internal.commLog
import kotlin.reflect.KClass

interface CommunicationInterface {
    fun getMtlsHttpClient(isProduction: Boolean, timeout: Long = 30_000L, token : String): HttpClient
    fun getHttpClient(isProduction: Boolean, timeout: Long = 30_000L, token : String): HttpClient
    var config          : MystyBagSDKConfig
    var isLoginSuccess  : Boolean
    var token           : String
    fun setOnUnauthorized(handler: (suspend () -> Boolean)?)
    suspend fun <Request: BaseRequestModel, Success: BaseResponseModel, Error: BaseResponseModel> sendCommand(request: Request, requestClass: KClass<Request>, successClass: KClass<Success>, errorClass: KClass<Error>):  CommandResult<Success, Error>
    suspend fun uploadFile(target: FileUploadTarget, fileData: ByteArray, fileName: String): CommandResult<UploadFileResponseModel, BaseResponseModel>
}

interface CommunicationProtocol {
    suspend fun sing(text: String): String
    suspend fun signRequest(request: String): String?
    suspend fun encryptRequest(request: String): String?
    fun kvSerial(serial: String): String
    fun logMessage(message: String)
}

expect object Communication: CommunicationInterface

internal class CommunicationImpl(private var isInternetAvailable: () -> Boolean) {

    companion object {
        private const val TIME_OUT_IN_MILLISECONDS  = 30_000L
    }

    private val COMMANDS_WITHOUT_LOGIN = listOf(
        GET_COUNTRIES_API,
        FORGOTTEN_PASSWORD_API,
        COMMAND_GET_WELCOME_IMAGES
    )

    private val LOGIN_APIS = listOf(
        USER_LOGIN,
        MERCHANT_LOGIN,
        STAFF_LOGIN
    )

    lateinit var config : MystyBagSDKConfig
    var isLoginSuccess  = false
    var token           = ""
    private var onUnauthorizedHandler: (suspend () -> Boolean)? = null

    fun setOnUnauthorized(handler: (suspend () -> Boolean)?) {
        onUnauthorizedHandler = handler
    }


    private val json by lazy { Json { ignoreUnknownKeys = true; encodeDefaults = true; isLenient = true; explicitNulls = false }  }
    private var commProtocol      : CommunicationProtocol? = null

    fun init(
        isProduction: Boolean,
        protocol: CommunicationProtocol?,
        companyBookApiKey: String = "",
    ) {
        config = MystyBagSDKConfig(
            isProduction = isProduction,
            companyBookApiKey = companyBookApiKey.trim(),
        )
        commProtocol = protocol
    }

    @Suppress("UNCHECKED_CAST")
    @OptIn(InternalSerializationApi::class)
    suspend fun <Request: BaseRequestModel, Success: BaseResponseModel, Error: BaseResponseModel> sendCommand(request: Request, requestClass: KClass<Request>, successClass: KClass<Success>, errorClass: KClass<Error>): CommandResult<Success, Error> {
        val result = if (!isInternetAvailable()) {
            commLog("sendCommand: no internet for api='${request.api}'")
            CommandResult.BaseError(BaseResponseModel(api = request.api, status = STATUS_NO_INTERNET_CONNECTION))
        } else
            try {
                request.timestamp   = getRequestTimestampMs().toString()
                val requestBody     = json.encodeToString(requestClass.serializer(), request)
                commLog("sendCommand: start api='${request.api}', production=${config.isProduction}, bodyLen=${requestBody.length}")

                val protocol = requireNotNull(commProtocol) { "CommunicationProtocol is not initialized" }
                val signature = withContext(Dispatchers.Default) {
                    requireNotNull(protocol.signRequest(requestBody)) { "Request signature generation failed" }
                }
                val encrypted = withContext(Dispatchers.Default) {
                    requireNotNull(protocol.encryptRequest("$requestBody##$signature")) { "Request encryption failed" }
                }

                var response = sendRestCommand(api = request.api, requestBody = encrypted, token = token)
                commLog("sendCommand: response status=${response.status.value} api='${request.api}'")
                var responseBody: String? = if (response.status == HttpStatusCode.OK) response.bodyAsText() else null

                // On 401: try re-login once and retry the request (do not retry for login APIs)
                if (responseBody == null && response.status == HttpStatusCode.Unauthorized &&
                    request.api !in LOGIN_APIS && onUnauthorizedHandler != null
                ) {
                    val refreshed = onUnauthorizedHandler!!.invoke()
                    if (refreshed) {
                        response = sendRestCommand(api = request.api, requestBody = encrypted, token = token)
                        commLog("sendCommand: response status=${response.status.value} api='${request.api}' (after re-login retry)")
                        responseBody = if (response.status == HttpStatusCode.OK) response.bodyAsText() else null
                    }
                }

                responseBody?.let { body ->
                    json.decodeFromString<BaseResponseModel>(body).let { baseResponse ->
                        when {
                            baseResponse.status == STATUS_OK                                    -> CommandResult.Success(json.decodeFromString(successClass.serializer(), body))
                            errorClass.simpleName == BaseResponseModel::class.simpleName        -> CommandResult.BaseError(BaseResponseModel(api = request.api, apiEndPoint = baseResponse.apiEndPoint, statusMessage = baseResponse.statusMessage, statusDetails = baseResponse.statusDetails, status = baseResponse.status))
                            else                                                                ->
                                try {
                                    CommandResult.Error(json.decodeFromString(errorClass.serializer(), body))
                                }
                                catch (exception: Exception) {
                                    exception.printStackTrace()
                                    CommandResult.BaseError(BaseResponseModel(api = request.api, apiEndPoint = baseResponse.apiEndPoint, statusMessage = baseResponse.statusMessage, statusDetails = baseResponse.statusDetails, status = baseResponse.status))
                                }
                        }
                    }
                } ?: run {
                    if ( response.status == HttpStatusCode.Unauthorized )
                        CommandResult.BaseError(BaseResponseModel(api = request.api, status = STATUS_TOKEN_IS_EXPIRED))
                    else {
                        // TEMP [GLOG]: surface the HTTP code so the on-screen ERR snackbar shows it. Revert later.
                        CommandResult.BaseError(BaseResponseModel(api = request.api, apiEndPoint = request.api, statusMessage = "[GLOG] http=${response.status.value} body=null", status = STATUS_EXCEPTION))
                    }
                }
            }
            catch (exception: CancellationException) {
                throw exception
            }
            catch (exception: Throwable) {
                commLog("sendCommand FAILED api='${request.api}': ${exception::class.simpleName} - ${exception.message}")
                commLog(exception.stackTraceToString().take(4000))
                // TEMP [GLOG]: surface the throwable so the on-screen ERR snackbar shows it. Revert later.
                CommandResult.BaseError(BaseResponseModel(api = request.api, apiEndPoint = request.api, statusMessage = "[GLOG] ${exception::class.simpleName}: ${exception.message}", status = STATUS_EXCEPTION))
            }

        return result as CommandResult<Success, Error>
    }

    private suspend fun getUserIP(): String = ModelsData.ip.takeIf { ip -> ip.isNotEmpty() } ?: Communication.getHttpClient(isProduction = false, token = Communication.token).get("https://api.ipify.org").bodyAsText()

    private suspend fun sendRestCommand(api: String, requestBody: String, token: String): HttpResponse {
        // Backend REST traffic uses a certificate-pinned client on Android/iOS
        // (server cert must chain to one of our trusted roots — anti-MITM). On web
        // the browser manages TLS and pinning is not possible, so it is plain HTTPS.
        val client = Communication.getMtlsHttpClient(
            isProduction = config.isProduction,
            token = token
        )

        return client.post(api) {
            contentType(ContentType.Application.Json)
            userAgent("ktor client")
            setBody(requestBody)
        }
    }

    suspend fun uploadFile(target: FileUploadTarget, fileData: ByteArray, fileName: String): CommandResult<UploadFileResponseModel, BaseResponseModel> {
        if (!isInternetAvailable()) {
            commLog("uploadFile: no internet for target=${target.serverPath}")
            return CommandResult.BaseError<BaseResponseModel>(BaseResponseModel(api = UPLOAD_FILE, status = STATUS_NO_INTERNET_CONNECTION)) as CommandResult<UploadFileResponseModel, BaseResponseModel>
        }
        val result = try {
            val client = Communication.getMtlsHttpClient(
                isProduction = config.isProduction,
                token = token,
                timeout = 60_000L
            )
            val response = client.submitFormWithBinaryData(
                url = UPLOAD_FILE,
                formData = formData {
                    append("target", target.serverPath)
                    append("file", fileData, Headers.build {
                        append(HttpHeaders.ContentDisposition, "form-data; name=\"file\"; filename=\"$fileName\"")
                    })
                }
            )
            val body = response.bodyAsText()
            commLog("uploadFile: response status=${response.status.value} target=${target.serverPath}")
            when (response.status) {
                HttpStatusCode.OK -> {
                    val parsed = json.decodeFromString<UploadFileResponseModel>(body)
                    if (parsed.status == STATUS_OK) {
                        CommandResult.Success(parsed)
                    } else {
                        CommandResult.BaseError<BaseResponseModel>(BaseResponseModel(api = UPLOAD_FILE, status = parsed.status))
                    }
                }
                HttpStatusCode.Unauthorized ->
                    CommandResult.BaseError<BaseResponseModel>(BaseResponseModel(api = UPLOAD_FILE, status = STATUS_TOKEN_IS_EXPIRED))
                else ->
                    CommandResult.BaseError<BaseResponseModel>(BaseResponseModel(api = UPLOAD_FILE, status = STATUS_EXCEPTION))
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            commLog("uploadFile FAILED target=${target.serverPath}: ${e::class.simpleName} - ${e.message}")
            CommandResult.BaseError<BaseResponseModel>(BaseResponseModel(api = UPLOAD_FILE, status = STATUS_EXCEPTION))
        }
        return result as CommandResult<UploadFileResponseModel, BaseResponseModel>
    }
}
